import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Calendar, X } from 'lucide-react';
import type { HeaderFilter } from '../../../contexts/DynamicHeaderContext';

interface DatePeriodFilterProps {
  filter: HeaderFilter;
}

/**
 * DatePeriodFilter - Filtro avanzato per date con supporto per:
 * - Selezione campo (created_at / signed_at)
 * - Anno intero
 * - Quarter (Q1-Q4)
 * - Range personalizzato
 */
export function DatePeriodFilter({ filter }: DatePeriodFilterProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const currentYear = new Date().getFullYear();
  const years = [currentYear, currentYear - 1, currentYear - 2, currentYear - 3];

  const dateField = filter.dateField || 'created_at';
  const periodType = filter.periodType || 'custom';
  const selectedYear = filter.selectedYear || currentYear;
  const selectedQuarter = filter.selectedQuarter;
  const dateFrom = filter.dateFrom || '';
  const dateTo = filter.dateTo || '';

  // Stato locale per i date picker (evita reset durante la digitazione)
  const [localDateFrom, setLocalDateFrom] = useState(dateFrom);
  const [localDateTo, setLocalDateTo] = useState(dateTo);

  // Sincronizza stato locale quando cambiano i props (es. click su preset)
  useEffect(() => {
    setLocalDateFrom(dateFrom);
  }, [dateFrom]);

  useEffect(() => {
    setLocalDateTo(dateTo);
  }, [dateTo]);

  // Calcola posizione del dropdown
  const updatePosition = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const dropdownWidth = 320;
      const dropdownHeight = 350;
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;

      let top = rect.bottom + 4;
      if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
        top = rect.top - dropdownHeight - 4;
      }

      const spaceRight = window.innerWidth - rect.left;
      let left = rect.left;

      if (spaceRight < dropdownWidth + 16) {
        left = rect.right - dropdownWidth;
      }

      left = Math.max(8, Math.min(left, window.innerWidth - dropdownWidth - 8));

      setPosition({
        top: Math.max(8, top),
        left
      });
    }
  };

  const handleToggle = () => {
    if (!isOpen) {
      updatePosition();
    }
    setIsOpen(!isOpen);
  };

  // Click outside to close
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (
        buttonRef.current && !buttonRef.current.contains(e.target as Node) &&
        dropdownRef.current && !dropdownRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [isOpen]);

  // Gestione cambio periodo
  const handlePeriodTypeChange = (type: 'year' | 'quarter' | 'custom') => {
    filter.onPeriodTypeChange?.(type);
    if (type !== 'custom') {
      // Reset date range quando si passa ad anno/quarter
      filter.onDateFromChange?.('');
      filter.onDateToChange?.('');
    }
    if (type === 'year') {
      // Reset quarter quando si passa ad anno
      filter.onQuarterChange?.(null);
    }
  };

  // Determina se ci sono filtri attivi
  const hasActiveFilter = () => {
    if (periodType === 'year') return true;
    if (periodType === 'quarter' && selectedQuarter) return true;
    if (periodType === 'custom' && (dateFrom || dateTo)) return true;
    return false;
  };

  // Label per il bottone
  const getButtonLabel = () => {
    const fieldLabel = dateField === 'signed_at' ? 'Firma' : 'Creazione';

    if (periodType === 'year') {
      return `${fieldLabel}: ${selectedYear}`;
    }
    if (periodType === 'quarter' && selectedQuarter) {
      return `${fieldLabel}: Q${selectedQuarter} ${selectedYear}`;
    }
    if (periodType === 'custom' && (dateFrom || dateTo)) {
      if (dateFrom && dateTo) {
        const fromFormatted = new Date(dateFrom).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' });
        const toFormatted = new Date(dateTo).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' });
        return `${fieldLabel}: ${fromFormatted} - ${toFormatted}`;
      }
      if (dateFrom) return `${fieldLabel}: Da ${new Date(dateFrom).toLocaleDateString('it-IT')}`;
      if (dateTo) return `${fieldLabel}: Fino a ${new Date(dateTo).toLocaleDateString('it-IT')}`;
    }
    return `Data ${fieldLabel.toLowerCase()}`;
  };

  // Reset filtri
  const clearFilters = () => {
    filter.onPeriodTypeChange?.('custom');
    filter.onQuarterChange?.(null);
    filter.onDateFromChange?.('');
    filter.onDateToChange?.('');
  };

  return (
    <>
      <button
        ref={buttonRef}
        onClick={handleToggle}
        className={`inline-flex items-center gap-1 px-2.5 h-7 text-xs rounded-md focus:outline-none transition-colors ${
          hasActiveFilter()
            ? 'bg-[rgba(233,30,140,0.08)] hover:bg-[rgba(233,30,140,0.13)]'
            : 'text-foreground hover:bg-black/5 dark:hover:bg-white/5'
        }`}
      >
        <Calendar className={`w-3 h-3 ${hasActiveFilter() ? 'text-[#E91E8C]' : ''}`} />
        <span className="truncate max-w-[150px]" style={hasActiveFilter() ? {
          background: 'linear-gradient(135deg, #E91E8C, #F97316)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
        } : {}}>
          {getButtonLabel()}
        </span>
        <ChevronDown className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''} ${hasActiveFilter() ? 'text-[#E91E8C]' : ''}`} />
      </button>

      {isOpen && createPortal(
        <div
          ref={dropdownRef}
          className="fixed w-80 rounded-lg shadow-xl bg-white dark:bg-gray-800 ring-1 ring-gray-200 dark:ring-white/10 z-[20050] flex flex-col"
          style={{ top: position.top, left: position.left }}
        >
          {/* Header */}
          <div className="p-3 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
                Filtra per data
              </span>
              {hasActiveFilter() && (
                <button
                  onClick={clearFilters}
                  className="text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 flex items-center gap-1"
                >
                  <X className="w-3 h-3" />
                  Pulisci
                </button>
              )}
            </div>

            {/* Selettore campo */}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => {
                  filter.onDateFieldChange?.('created_at');
                }}
                className={`flex-1 px-3 py-1.5 text-xs rounded-md transition-colors ${
                  dateField === 'created_at'
                    ? 'bg-[rgba(233,30,140,0.08)] text-[#E91E8C] font-medium'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                Data creazione
              </button>
              <button
                type="button"
                onClick={() => {
                  filter.onDateFieldChange?.('signed_at');
                }}
                className={`flex-1 px-3 py-1.5 text-xs rounded-md transition-colors ${
                  dateField === 'signed_at'
                    ? 'bg-[rgba(233,30,140,0.08)] text-[#E91E8C] font-medium'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                Data firma
              </button>
            </div>
          </div>

          {/* Selettore periodo */}
          <div className="p-3 border-b border-gray-200 dark:border-gray-700">
            <div className="flex gap-2 mb-3">
              <button
                onClick={() => handlePeriodTypeChange('year')}
                className={`flex-1 px-3 py-1.5 text-xs rounded-md transition-colors ${
                  periodType === 'year'
                    ? 'bg-gray-900 dark:bg-white text-white dark:text-gray-900 font-medium'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                Anno
              </button>
              <button
                onClick={() => handlePeriodTypeChange('quarter')}
                className={`flex-1 px-3 py-1.5 text-xs rounded-md transition-colors ${
                  periodType === 'quarter'
                    ? 'bg-gray-900 dark:bg-white text-white dark:text-gray-900 font-medium'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                Trimestre
              </button>
              <button
                onClick={() => handlePeriodTypeChange('custom')}
                className={`flex-1 px-3 py-1.5 text-xs rounded-md transition-colors ${
                  periodType === 'custom'
                    ? 'bg-gray-900 dark:bg-white text-white dark:text-gray-900 font-medium'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                Personalizzato
              </button>
            </div>

            {/* Selettore anno (per anno e quarter) */}
            {(periodType === 'year' || periodType === 'quarter') && (
              <div className="mb-3">
                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1.5">
                  Seleziona anno
                </label>
                <div className="flex gap-2">
                  {years.map(year => (
                    <button
                      key={year}
                      onClick={() => filter.onYearChange?.(year)}
                      className={`flex-1 px-2 py-1.5 text-xs rounded-md transition-colors ${
                        selectedYear === year
                          ? 'bg-[rgba(233,30,140,0.08)] text-[#E91E8C] font-medium'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                      }`}
                    >
                      {year}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Selettore quarter */}
            {periodType === 'quarter' && (
              <div>
                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1.5">
                  Seleziona trimestre
                </label>
                <div className="flex gap-2">
                  {([1, 2, 3, 4] as const).map(q => (
                    <button
                      key={q}
                      onClick={() => filter.onQuarterChange?.(q)}
                      className={`flex-1 px-2 py-2 text-xs rounded-md transition-colors ${
                        selectedQuarter === q
                          ? 'bg-[rgba(233,30,140,0.08)] text-[#E91E8C] font-medium'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                      }`}
                    >
                      <div className="font-medium">Q{q}</div>
                      <div className="text-[10px] opacity-70">
                        {q === 1 && 'Gen-Mar'}
                        {q === 2 && 'Apr-Giu'}
                        {q === 3 && 'Lug-Set'}
                        {q === 4 && 'Ott-Dic'}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Date picker personalizzate */}
            {periodType === 'custom' && (
              <div className="flex gap-2 items-center">
                <div className="flex-1">
                  <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                    Da
                  </label>
                  <input
                    type="date"
                    value={localDateFrom}
                    onChange={(e) => {
                      const val = e.target.value;
                      setLocalDateFrom(val);
                      // Propaga al parent solo se è una data valida completa (YYYY-MM-DD con anno a 4 cifre)
                      if (/^\d{4}-\d{2}-\d{2}$/.test(val) || val === '') {
                        filter.onDateFromChange?.(val);
                      }
                    }}
                    className="w-full px-2 py-1.5 text-xs border border-gray-200 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#E91E8C]"
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                    A
                  </label>
                  <input
                    type="date"
                    value={localDateTo}
                    onChange={(e) => {
                      const val = e.target.value;
                      setLocalDateTo(val);
                      // Propaga al parent solo se è una data valida completa (YYYY-MM-DD con anno a 4 cifre)
                      if (/^\d{4}-\d{2}-\d{2}$/.test(val) || val === '') {
                        filter.onDateToChange?.(val);
                      }
                    }}
                    className="w-full px-2 py-1.5 text-xs border border-gray-200 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#E91E8C]"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Footer con quick presets */}
          <div className="p-3">
            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-2">
              Scorciatoie
            </label>
            <div className="flex flex-wrap gap-1.5">
              <button
                onClick={() => {
                  handlePeriodTypeChange('custom');
                  const today = new Date().toISOString().split('T')[0];
                  filter.onDateFromChange?.(today);
                  filter.onDateToChange?.(today);
                }}
                className="px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded hover:bg-gray-200 dark:hover:bg-gray-600"
              >
                Oggi
              </button>
              <button
                onClick={() => {
                  handlePeriodTypeChange('custom');
                  const today = new Date();
                  const weekAgo = new Date(today);
                  weekAgo.setDate(weekAgo.getDate() - 7);
                  filter.onDateFromChange?.(weekAgo.toISOString().split('T')[0]);
                  filter.onDateToChange?.(today.toISOString().split('T')[0]);
                }}
                className="px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded hover:bg-gray-200 dark:hover:bg-gray-600"
              >
                Ultimi 7 giorni
              </button>
              <button
                onClick={() => {
                  handlePeriodTypeChange('custom');
                  const today = new Date();
                  const monthAgo = new Date(today);
                  monthAgo.setDate(monthAgo.getDate() - 30);
                  filter.onDateFromChange?.(monthAgo.toISOString().split('T')[0]);
                  filter.onDateToChange?.(today.toISOString().split('T')[0]);
                }}
                className="px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded hover:bg-gray-200 dark:hover:bg-gray-600"
              >
                Ultimi 30 giorni
              </button>
              <button
                onClick={() => {
                  handlePeriodTypeChange('custom');
                  const today = new Date();
                  const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
                  filter.onDateFromChange?.(startOfMonth.toISOString().split('T')[0]);
                  filter.onDateToChange?.(today.toISOString().split('T')[0]);
                }}
                className="px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded hover:bg-gray-200 dark:hover:bg-gray-600"
              >
                Questo mese
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
